import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Alert,
} from "@mui/material";
import { Warning } from "@mui/icons-material";
import { IImpersonatableUser } from "../../interfaces/impersonation";

interface ConfirmationModalProps {
  open: boolean;
  user: IImpersonatableUser | null;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
}

const getEntityLabel = (entityType: string): string => {
  const labels: Record<string, string> = {
    internal: "Internal",
    marketplace: "Marketplace",
    merchant: "Merchant",
    ecommerce: "Ecommerce",
  };
  return labels[entityType.toLowerCase()] || entityType;
};

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  open,
  user,
  onConfirm,
  onCancel,
  loading,
}) => {
  if (!user) return null;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Warning color="warning" />
          <Typography variant="h6">Confirmar Impersonación</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Estás a punto de ingresar a la plataforma como el siguiente usuario:
        </Typography>

        <Paper
          variant="outlined"
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
            backgroundColor: "#fafafa",
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {user.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user.email}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Entidad: {user.entity.name} (CUIT: {user.entity.cuit})
            </Typography>
            <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
              <Chip
                label={getEntityLabel(user.entity.type)}
                size="small"
                color="primary"
              />
              {user.roles.map((role) => (
                <Chip
                  key={role.id}
                  label={role.name}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        </Paper>

        <Alert severity="info" sx={{ mt: 2 }}>
          Serás redirigido al inicio de la plataforma con los permisos de este
          usuario. Podrás salir de la impersonación en cualquier momento desde
          el banner superior.
        </Alert>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onCancel} disabled={loading} color="inherit">
          Cancelar
        </Button>
        <Button
          onClick={onConfirm}
          disabled={loading}
          variant="contained"
          color="primary"
          startIcon={
            loading ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {loading ? "Ingresando..." : "Confirmar Impersonación"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
